import React, { FC } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useTranslation } from '@blocs.i18n';
import CloudinaryPhoto from '@minecraft.cloudinary-photo';

import { MediaType } from '../../../shared/types';
import getRouterWithPrefix from '../../../shared/utils/getRouterWithPrefix';
import {
  Header,
  StyledHeaderControl,
  HeaderLogo,
  BackHomeBlock,
  ControlBlockTitle,
  ControlBlockName,
  ChevronLeft,
  ProjectBlock,
  PresentationBlock,
  CompanyName,
  Logo,
  LogoPlaceholder,
} from './RecipientHeaderStyles';

type Props = {
  displayBackToHomeLink?: boolean;
  presentationTypeName: string;
  presentationName: string;
  projectName?: string;
  logo: MediaType;
  companyName?: string;
  parentCompanyName?: string;
  routerPrefix?: string;
  className?: string;
};

const RecipientHeader: FC<Props> = ({
  displayBackToHomeLink = true,
  presentationTypeName,
  presentationName,
  projectName,
  logo,
  companyName,
  parentCompanyName,
  routerPrefix,
  className,
}) => {
  const { t } = useTranslation();
  const { guid } = useParams<{ guid: string }>();

  const firstLetterOfCompanyName = companyName?.charAt(0) || '';
  const logoComponent = logo?.fileKey ? (
    <CloudinaryPhoto autoRotateDimensions={false} fileKey={logo?.fileKey} crop="fit" customSize={[111, 53]} />
  ) : (
    <LogoPlaceholder>
      <span>{firstLetterOfCompanyName}</span>
    </LogoPlaceholder>
  );
  const companyNamePrefixWithSuffix = parentCompanyName ? `${parentCompanyName} - ` : '';

  return (
    <Header className={className} data-testid="presentation-header-wrapper">
      <StyledHeaderControl>
        {displayBackToHomeLink && (
          <BackHomeBlock>
            <Link to={getRouterWithPrefix(routerPrefix, `${guid}`)}>
              <ChevronLeft inheritColor name="chevron-left-thick" />
              {t('common:button.backToHome')}
            </Link>
          </BackHomeBlock>
        )}
        {projectName && (
          <ProjectBlock data-testid="project-name-wrap">
            <ControlBlockTitle>{t('common:recipientGridView.type.project')}</ControlBlockTitle>
            <ControlBlockName $withProjectName>{projectName}</ControlBlockName>
          </ProjectBlock>
        )}
        {presentationTypeName && presentationName && (
          <PresentationBlock data-testid="presentation-name-wrap">
            <ControlBlockTitle>{presentationTypeName}</ControlBlockTitle>
            <ControlBlockName $withProjectName={!!projectName}>{presentationName}</ControlBlockName>
          </PresentationBlock>
        )}
      </StyledHeaderControl>
      <HeaderLogo data-testid="org-wrap">
        <Logo data-testid="logo-wrap">{logoComponent}</Logo>
        <CompanyName>
          {companyNamePrefixWithSuffix}
          {companyName || ''}
        </CompanyName>
      </HeaderLogo>
    </Header>
  );
};

export default RecipientHeader;
